/**
 * Version Notification Component
 * 
 * Shows a notification to users who haven't seen the latest version
 */

import { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { VersionDetailsDialog } from '@/pages/Home/components/VersionDetailsDialog';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { cn } from '@/lib/utils';

interface VersionNotificationProps {
  className?: string;
}

export const VersionNotification = ({ className }: VersionNotificationProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  
  const { hasUpdate, latestVersion, currentVersion, markAsSeen } = useVersionCheck({
    pollingInterval: 120000,
    checkOnMount: true,
  });
  
  // Show notification if there's an update and user hasn't seen it
  useEffect(() => {
    // Show notification if:
    // 1. There's a new version (version differs from current) OR
    // 2. User hasn't seen the latest version (hasSeenVersion is false)
    if (latestVersion && latestVersion.hasSeenVersion === false) {
      setShowNotification(true);
    }
  }, [hasUpdate, latestVersion, currentVersion]);
  
  const handleViewDetails = () => {
    setShowDialog(true);
    setShowNotification(false);
  };
  
  const handleDismiss = () => {
    setShowNotification(false);
    // Optionally mark as seen when dismissing
    if (latestVersion) {
      markAsSeen();
    }
  };
  
  if (!showNotification || !latestVersion) return null;
  
  const isNewVersion = latestVersion.version !== currentVersion;
  
  return (
    <>
      <Card className={cn(
        "fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-5 shadow-lg",
        className
      )}>
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {isNewVersion ? 'إصدار جديد متاح!' : 'تحديثات جديدة!'}
                </p>
                <p className="text-xs text-muted-foreground">
                  الإصدار {latestVersion.version} يحتوي على تحسينات ومميزات جديدة
                </p>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    size="sm"
                    onClick={handleViewDetails}
                    className="h-7 px-3 text-xs"
                  >
                    عرض التفاصيل
                  </Button>
                  {!latestVersion.isForced && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleDismiss}
                      className="h-7 px-3 text-xs"
                    >
                      لاحقاً
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>
      
      {/* Version Details Dialog */}
      {latestVersion && (
        <VersionDetailsDialog
          open={showDialog}
          onOpenChange={(open) => {
            setShowDialog(open);
            // Mark as seen when closing the dialog
            if (!open && latestVersion.hasSeenVersion === false) {
              markAsSeen();
            }
          }}
          version={latestVersion.version}
        />
      )}
    </>
  );
};